import { Link } from 'react-router-dom';

import UserMenu from '@/components/UserMenu';
import { useStories, useUserStoryProgress } from '@/features/stories/useStories';
import { useAuthStore } from '@/stores/authStore';
import { getVersionDisplay } from '@/utils/version';

const genreColors: Record<string, string> = {
  fantasy: 'from-purple-600 to-indigo-600',
  horror: 'from-red-600 to-rose-600',
  mystery: 'from-amber-600 to-yellow-600',
  'sci-fi': 'from-cyan-600 to-blue-600',
};

const genreIcons: Record<string, string> = {
  fantasy: '\u2728', // sparkles
  horror: '\uD83D\uDC80', // skull
  mystery: '\uD83D\uDD0D', // magnifying glass
  'sci-fi': '\uD83D\uDE80', // rocket
};

export default function HomePage() {
  const { data: stories, isLoading, error } = useStories();
  const { user } = useAuthStore();
  const { data: progressStoryIds } = useUserStoryProgress(user?.userId);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-purple-300 text-xl">Loading stories...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-red-400 text-xl">Failed to load stories</div>
      </div>
    );
  }

  // Filter to only show ready stories
  const readyStories = stories?.filter((s) => s.status === 'ready') ?? [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="relative z-20 border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Yarn Tales</h1>
            <p className="text-purple-300 mt-1">Branching Fiction, Your Way</p>
          </div>
          <UserMenu />
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-semibold text-white mb-6">Begin Your Story</h2>

        {readyStories.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-purple-300 text-lg">No stories available yet.</p>
            <p className="text-purple-400 mt-2">Check back soon for new adventures!</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {readyStories.map((story) => {
              const hasProgress = progressStoryIds?.has(story.storyId) ?? false;
              const colorClass = genreColors[story.genre] || 'from-gray-600 to-slate-600';
              const icon = genreIcons[story.genre] || '\uD83D\uDCDA'; // default book

              return (
                <Link
                  key={story.storyId}
                  to={`/story/${story.storyId}`}
                  className="group relative overflow-hidden rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 transition-all hover:scale-[1.02] hover:shadow-xl"
                >
                  {/* Genre gradient header */}
                  <div
                    className={`h-24 bg-gradient-to-r ${colorClass} flex items-center justify-center`}
                  >
                    <span className="text-5xl">{icon}</span>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-xl font-semibold text-white group-hover:text-purple-300 transition-colors">
                        {story.title}
                      </h3>
                      {hasProgress && (
                        <span className="shrink-0 px-2 py-0.5 text-xs font-medium bg-green-500/20 text-green-400 rounded-full">
                          In Progress
                        </span>
                      )}
                    </div>

                    <p className="mt-2 text-sm text-purple-300/80 line-clamp-3">
                      {story.description}
                    </p>

                    <div className="mt-4 text-xs text-purple-400">
                      <span className="capitalize">{story.genre}</span>
                    </div>
                  </div>

                  {/* Hover indicator */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                </Link>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-auto">
        <div className="max-w-6xl mx-auto px-4 py-6 flex items-center justify-between text-purple-400 text-sm">
          <p>Powered by AI-generated branching fiction</p>
          <span className="text-xs text-purple-500/60">{getVersionDisplay()}</span>
        </div>
      </footer>
    </div>
  );
}
